import { useEffect } from 'react'
import firebase from 'firebase/app'
import { useDispatch } from 'react-redux'
import { updateAuthorization, setDisplayName, setAdmin, setTheme, setGuestSession } from '../reducers/auth/auth'
import { useSelector } from './useSelector'
import { GamePlayer } from '@prometheus/types'
import { applyTheme } from '../utils/theme'
import { readGuestSession } from '../utils/guestSession'

export default function useAuthState() {
  const dispatch = useDispatch()
  const uid = useSelector(s => s.auth.uid)
  const guest = useSelector(s => s.auth.guest)

  useEffect(() => {
    // Restore a stored guest session first, so someone who never signed in
    // comes back to their games instead of landing on a signed-out lobby. A
    // real account, if there is one, takes over from here (see the reducer).
    dispatch(setGuestSession(readGuestSession()))

    return firebase.auth().onAuthStateChanged((user) => {
      dispatch(updateAuthorization(user))
    })
  }, [dispatch])

  useEffect(() => {
    // Guests have no Firebase login, so firestore is closed to them from the
    // browser; their profile arrives with their identity off the socket, and
    // changes to it go back through the server (`updateProfile`).
    if (!uid || guest) {
      return
    }

    firebase.firestore().collection('users').doc(uid).get().then((res) => {
      const player = res.data() as GamePlayer | undefined
      dispatch(setDisplayName(player?.name))
      if (player?.admin) {
        dispatch(setAdmin())
      }
      // Reconcile theme from the saved profile; falls through to the cached
      // localStorage theme applied at boot when the profile has none.
      if (player?.theme) {
        dispatch(setTheme(player.theme))
        applyTheme(player.theme)
      }
    })
  }, [uid, guest, dispatch])
}
